/**
 * This component contains code to add a machine, this component is accessible from multiple screens
 * serialNumber should be 13 character long.
 */
import {
  Typography,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Container,
  MenuItem,
  Box,
  IconButton,
  InputLabel
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import { Controller } from "react-hook-form";
import useForm from "hooks/useForm";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import "./AddMachine.scss";
import DatePicker from "react-datepicker";
import Settings from "@mui/icons-material/Settings";
import Close from "@mui/icons-material/Close";
import { AddMachineForm } from "./AddMachine.types";
import MachineUse from "types/MachineType.enum";
import {
  useAddMachine,
  useClinics,
  useMachineHardwareVersion
} from "services/machines.service";
import DashedDivider from "common/ui/DashedDivider";
import QueryStatus from "types/QueryStatus.enum";
import yup from "helpers/validator";
import { yupResolver } from "@hookform/resolvers/yup";
import { setAddMachine } from "context/modal.context";
import { useAppDispatch, useAppSelector } from "context/hooks";
import { selectAddMachine } from "context/modal.selector";
import LoadingButton from "common/ui/LoadingButton";
import { setHasNewMachine } from "context/machine.context";

function AddMachine() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const props = useAppSelector(selectAddMachine);

  const machineFormSchema = yup.object().shape({
    machineUse: yup
      .mixed<MachineUse>()
      .oneOf([MachineUse.clinical, MachineUse.nonClinical])
      .required(),
    clinic: yup
      .string()
      .nullable()
      .when("machineUse", {
        is: MachineUse.nonClinical,
        then: (schema) => schema.transform(() => null)
      })
      .transform((val) => val || null),
    serial: yup
      .string()
      .required(t("addMachine.serialRequired"))
      .matches(/^[a-zA-Z0-9]+$/g, t("addMachine.serialAlphaNum"))
      .length(13, t("addMachine.serialCharacterLimit")),
    machineName: yup
      .string()
      .matches(/^[a-zA-Z0-9/-\s()_]*$/g, t("addMachine.machineAlphaNum"))
      .max(50, t("addMachine.machineNameLimit", { count: 50 }))
      .when("serial", ([serial], schema) => {
        return schema.transform((val) => val?.trim() || serial);
      }),
    serviceDue: yup.date().nullable()
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors }
  } = useForm<AddMachineForm>({
    defaultValues: {
      machineUse: MachineUse.clinical,
      clinic: "",
      serial: "",
      machineName: "",
      serviceDue: null
    },
    resolver: yupResolver(machineFormSchema)
  });
  const machineUse = watch("machineUse");
  const serial = watch("serial");

  const clinics = useClinics();
  const machineHardwareVersion = useMachineHardwareVersion();

  const addNewMachine = useAddMachine(onMachineAdded);

  function onModalDismiss() {
    reset();
    dismiss();
  }

  function dismiss() {
    dispatch(
      setAddMachine({
        open: false
      })
    );
  }

  function onSubmit(data: AddMachineForm) {
    addNewMachine.mutate({
      machineUseId: data.machineUse,
      name: data.machineName,
      serialNumber: data.serial,
      organisationSiteId: data.clinic,
      serviceDue: data.serviceDue?.toISOString()
    });
  }

  function determineHardwareVer() {
    if (serial === "") {
      return "";
    } else {
      const version = machineHardwareVersion.data?.find((item) =>
        serial.toLowerCase().includes(item.serialNumberPrefix.toLowerCase())
      );
      if (version) {
        return version.name;
      } else {
        return t("common.na");
      }
    }
  }

  function onMachineAdded(_data, error) {
    let message = t("addMachine.machineAddedSuccessfully");

    if (!error) {
      toast.success(message);
    }
    dispatch(setHasNewMachine(true));
    dismiss();
    reset();
  }

  return (
    <Dialog open={props.open} scroll="body" className="add-machine">
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" alignItems="center">
            <Settings color="gray" sx={{ marginRight: 1 }} />
            <Typography
              color="gray.main"
              variant="h6"
              fontWeight="medium"
              alignSelf="center"
            >
              {t("addMachine.title")}
            </Typography>
          </Stack>
          <IconButton onClick={onModalDismiss}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack direction="row">
          <Controller
            control={control}
            name="machineUse"
            render={({ field: { value, onChange } }) => (
              <ToggleButtonGroup
                exclusive
                fullWidth
                onChange={(_, newValue) =>
                  newValue !== null && onChange(newValue)
                }
                value={value}
              >
                <ToggleButton
                  value={MachineUse.clinical}
                  classes={{
                    root: "add-machine__toggle-button",
                    selected: "add-machine__toggle-button--active"
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t("addMachine.clinical")}
                  </Typography>
                </ToggleButton>
                <ToggleButton
                  value={MachineUse.nonClinical}
                  classes={{
                    root: "add-machine__toggle-button",
                    selected: "add-machine__toggle-button--active"
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t("addMachine.nonClinical")}
                  </Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          />
        </Stack>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Container maxWidth="xl" sx={{ marginBottom: 2 }}>
            <InputLabel htmlFor="serial" required>
              {t("addMachine.serial")}
            </InputLabel>
            <TextField
              id="serial"
              fullWidth
              type="text"
              {...register("serial")}
              helperText={errors.serial?.message}
              error={!!errors.serial}
            />

            <InputLabel htmlFor="hardware" required>
              {t("addMachine.hardware")}
            </InputLabel>
            <TextField
              id="hardware"
              value={determineHardwareVer()}
              fullWidth
              variant="outlined"
              disabled
              InputProps={{
                readOnly: true
              }}
              sx={{ backgroundColor: "grey.100" }}
            />

            {/* hidden clinic field as obsolete, remove false to restore the changes */}
            {false &&
            machineUse === MachineUse.clinical &&
            clinics.status === QueryStatus.success ? (
              <>
                <InputLabel htmlFor="clinic">
                  {t("addMachine.clinic")}
                </InputLabel>
                <Controller
                  control={control}
                  name="clinic"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      inputProps={{ id: "clinic" }}
                      select
                      fullWidth
                      name="clinic"
                      SelectProps={{
                        displayEmpty: true,
                        MenuProps: { sx: { maxHeight: 300 } }
                      }}
                    >
                      <MenuItem value="">{t("common.na")}</MenuItem>
                      {clinics.data.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </>
            ) : null}

            <InputLabel htmlFor="machineName">
              {t("addMachine.machineName")}
            </InputLabel>
            <TextField
              fullWidth
              {...register("machineName")}
              id="machineName"
              helperText={errors.machineName?.message}
              error={!!errors.machineName}
            />

            <InputLabel>{t("addMachine.serviceDue")}</InputLabel>
            <Controller
              control={control}
              name="serviceDue"
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  onChange={onChange}
                  selected={value}
                  inline
                  disabledKeyboardNavigation
                />
              )}
            />
          </Container>

          <DashedDivider />

          <Box display="flex" justifyContent="center" margin={2}>
            <LoadingButton
              type="submit"
              variant="contained"
              size="large"
              classes={{ root: "add-machine__save-button" }}
              startIcon={<DoneIcon />}
              loading={addNewMachine.isLoading}
            >
              {t("common.save")}
            </LoadingButton>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddMachine;
